/* global axios */
import { rememberConversation, restoreConversation } from './lastConversation';
import { createRouter, createWebHistory } from 'vue-router';

import { frontendURL } from '../helper/URLHelper';
import dashboard from './dashboard/dashboard.routes';
import store from 'dashboard/store';
import { validateLoggedInRoutes } from '../helper/routeHelpers';
import { isOnOnboardingView } from 'v3/helpers/RouteHelper';
import AnalyticsHelper from '../helper/AnalyticsHelper';

const ONBOARDING_STEPS = ['account_details', 'enrichment'];
const BILLING_ROUTE_NAME = 'billing_settings_index';
const routes = [...dashboard.routes];

const onboardingPath = step =>
  step === 'inbox_setup' ? 'onboarding/inbox-setup' : 'onboarding';

export const router = createRouter({ history: createWebHistory(), routes });

const parseDateOnly = value => {
  if (!value) return null;

  if (typeof value === 'string') {
    const [datePart] = value.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    if (year && month && day) return new Date(year, month - 1, day);
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const isAccountExpired = account => {
  if (typeof account?.access_expired === 'boolean') {
    return account.access_expired;
  }

  const expiryDate = parseDateOnly(account?.access_expires_at);
  if (!expiryDate) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  expiryDate.setHours(0, 0, 0, 0);

  return expiryDate < today;
};

const isBillingRoute = route => route.name === BILLING_ROUTE_NAME;

export const validateAuthenticateRoutePermission = async (to, next) => {
  const { isLoggedIn, getCurrentUser: user } = store.getters;

  if (!isLoggedIn) {
    window.location.assign('/app/login');
    return '';
  }

  const { accounts = [], account_id: accountId } = user;

  if (!accounts.length) {
    if (to.name === 'no_accounts') {
      return next();
    }
    return next(frontendURL('no-accounts'));
  }

  const routeAccountId = Number(to.params?.accountId || accountId);
  const userAccount = accounts.find(a => a.id === routeAccountId);
  const isAdmin = userAccount?.role === 'administrator';
  const isActive = userAccount?.status === 'active';
  const isSuperAdmin = user.type === 'SuperAdmin';
  const isExpired = !isSuperAdmin && isActive && isAccountExpired(userAccount);
  const needsOnboarding =
    !isSuperAdmin &&
    ONBOARDING_STEPS.includes(userAccount?.onboarding_step) &&
    isAdmin &&
    isActive;

  if (isExpired) {
    if (isAdmin) {
      if (!isBillingRoute(to)) {
        return next(frontendURL(`accounts/${routeAccountId}/settings/billing`));
      }

      return next();
    }

    if (to.name !== 'account_suspended') {
      return next(frontendURL(`accounts/${routeAccountId}/suspended`));
    }

    return next();
  }

  if (to.name === 'no_accounts' || !to.name) {
    let target = 'dashboard';
    if (isExpired && isAdmin) {
      target = 'settings/billing';
    } else if (needsOnboarding) {
      target = 'onboarding';
    }

    return next(frontendURL(`accounts/${routeAccountId}/${target}`));
  }

  if (needsOnboarding && !isOnOnboardingView(to)) {
    return next(
      frontendURL(
        `accounts/${routeAccountId}/${onboardingPath(userAccount?.onboarding_step)}`
      )
    );
  }
  if (!needsOnboarding && isOnOnboardingView(to)) {
    return next(frontendURL(`accounts/${routeAccountId}/dashboard`));
  }

  const nextRoute = validateLoggedInRoutes(to, store.getters.getCurrentUser);
  return nextRoute ? next(frontendURL(nextRoute)) : next();
};

export const initalizeRouter = () => {
  const userAuthentication = store.dispatch('setUser');

  router.beforeEach(async (to, from, next) => {
    AnalyticsHelper.page(to.name || '', {
      path: to.path,
      name: to.name,
    });

    if (to.meta?.public) {
      return next();
    }

    await userAuthentication;
    return validateAuthenticateRoutePermission(
      to,
      async target => {
        if (target !== undefined) return next(target);
        const restored = await restoreConversation(
          to,
          from,
          store.getters.getCurrentUser?.id,
          store.getters.getUISettings?.remember_last_conversation === true,
          (accountId, conversationId) =>
            axios.get(
              `/api/v1/accounts/${accountId}/conversations/${conversationId}`
            )
        );
        return restored ? next(restored) : next();
      },
      store
    );
  });
  router.afterEach((to, _from, failure) => {
    if (!failure && store.getters.isLoggedIn) {
      rememberConversation(
        to,
        store.getters.getCurrentUser?.id,
        store.getters.getUISettings?.remember_last_conversation === true
      );
    }
  });
};

export default router;
